import { Fragment, ReactElement, useEffect, useMemo } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActCategory } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useEventCallback } from '@mui/material';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { transformDeath } from 'notifications-from-civil-status/utils/transform-death';
import RegistrationDeathNonResidentPage from 'cancellations/pages/cancellation-non-resident/cancellation';
import { CancellationNonResident } from 'cancellations/view-models/cancellation-non-resident';

interface CancellationNonResidentNotificationSharedProps {
  goBackUrl?: string;
  goBackUrlDetail?: string;
  items?: BreadcrumbItem[];
  isOther?: boolean;
  editSipoUrl?: string;
}

const CancellationNonResidentNotificationShared = ({
  goBackUrl = '',
  goBackUrlDetail = '',
  items = [],
  isOther,
  editSipoUrl = '',
}: CancellationNonResidentNotificationSharedProps): ReactElement => {
  const params = useParams();

  const idNotifica = params.idNotifica || '';

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const [dataEvent, getDataEvent] = useHttpFetch<
    HttpResponse<ActCategory>,
    CancellationNonResident
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => transformDeath(res).data,
  });

  useEffect(() => {
    if (idAnsc && !isOther) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const navigate = useNavigate();

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlDetail);
    });
  });

  const onHandleExit = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.DaRegistrare,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <RegistrationDeathNonResidentPage
          isWithoutBreadcrumbs
          initialValues={(dataEvent.data || {}) as CancellationNonResident}
          onConfirm={onConfirm}
          onHandleExit={onHandleExit}
          disabledFromAct
          goBackUrl={goBackUrl}
          editSipoUrl={editSipoUrl}
        />
      ) : undefined}
    </Fragment>
  );
};

CancellationNonResidentNotificationShared.displayName =
  'Research Death Detail Notifications Page component';

export default CancellationNonResidentNotificationShared;
